import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, str, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints, ApiError } from '../api/client.js';
import { navigate } from '../router.js';
import type { PasswordPolicyDto } from '../api/types.js';
import type { SsoTextField } from '../components/sso-text-field.js';
import '../components/sso-card.js';
import '../components/sso-text-field.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';

/**
 * `<reset-password-page>` — set a new password from an emailed token (SSO-MOCKUPS.md §9b).
 *
 * Also serves **activation / onboarding** (first password) when mounted at `/activate` or with
 * `?mode=activate`; the only difference is the wording. The token comes from the email link
 * (`?token=…`) and is validated up front; the SPA never stores it.
 *
 * Password policy (mirrors the mockup checklist): ≥ 12 chars, upper + lower + a digit. The server
 * is authoritative (BCrypt/Argon2); these client checks are guidance + a strength meter.
 */
@customElement('reset-password-page')
@localized()
export class ResetPasswordPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      form {
        display: grid;
        gap: var(--sso-space);
      }
      .meter {
        height: 6px;
        border-radius: 3px;
        background: var(--sso-color-border);
        overflow: hidden;
      }
      .meter > span {
        display: block;
        height: 100%;
        transition: width 150ms ease, background-color 150ms ease;
      }
      .meter-label {
        font-size: 0.85rem;
        color: var(--sso-color-muted);
        margin: 0;
      }
      ul.rules {
        list-style: none;
        padding: 0;
        margin: 0;
        font-size: 0.9rem;
      }
      ul.rules li {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        color: var(--sso-color-muted);
      }
      ul.rules li.ok {
        color: var(--sso-color-success);
      }
    `,
  ];

  @state() private mode: 'reset' | 'activate' = 'reset';
  @state() private token = '';
  @state() private validating = true;
  @state() private tokenValid = false;
  @state() private busy = false;
  @state() private error = '';
  @state() private done = false;
  @state() private pw = '';
  @state() private pw2 = '';
  // Fallback mirrors the server defaults (Sso:PasswordPolicy); replaced by the fetched values.
  @state() private policy: PasswordPolicyDto = { minLength: 12, maxLength: 72 };

  @query('sso-text-field[name="pw"]') private pwField?: SsoTextField;
  @query('sso-text-field[name="pw2"]') private pw2Field?: SsoTextField;

  override connectedCallback(): void {
    super.connectedCallback();
    const params = new URLSearchParams(location.search);
    this.token = params.get('token') ?? '';
    this.mode =
      location.pathname.startsWith('/activate') || params.get('mode') === 'activate'
        ? 'activate'
        : 'reset';

    // Validate the token up front (non-consuming) so an expired/invalid link shows the error BEFORE the
    // user fills in a new password — rather than only failing on submit. A network error falls back to
    // optimistic (show the form); the reset/activate POST still re-checks authoritatively.
    if (this.token.length === 0) {
      this.validating = false;
      this.tokenValid = false;
    } else {
      const check =
        this.mode === 'activate'
          ? endpoints.validateActivationToken(this.token)
          : endpoints.validateResetToken(this.token);
      void check
        .then((r) => {
          this.tokenValid = r.valid;
        })
        .catch(() => {
          this.tokenValid = true; // couldn't reach the check — let the submit gate it
        })
        .finally(() => {
          this.validating = false;
        });
    }

    // Anonymous endpoint; on failure the fallback defaults stay (the server re-validates anyway).
    void endpoints
      .passwordPolicy()
      .then((p) => {
        this.policy = p;
      })
      .catch(() => {});
  }

  override firstUpdated(): void {
    this.focusFirstControl();
  }

  /* --- password policy helpers --- */
  private get hasLength(): boolean {
    return this.pw.length >= this.policy.minLength;
  }
  private get withinMax(): boolean {
    return this.pw.length <= this.policy.maxLength;
  }
  private get hasMixedCase(): boolean {
    return /[a-z]/.test(this.pw) && /[A-Z]/.test(this.pw);
  }
  private get hasDigit(): boolean {
    return /\d/.test(this.pw);
  }
  private get strength(): number {
    // 0..4 rough score for the meter.
    let s = 0;
    if (this.pw.length >= 8) s++;
    if (this.hasLength) s++;
    if (this.hasMixedCase) s++;
    if (this.hasDigit) s++;
    if (/[^A-Za-z0-9]/.test(this.pw)) s++;
    return Math.min(s, 4);
  }
  private get meetsPolicy(): boolean {
    return this.hasLength && this.withinMax && this.hasMixedCase && this.hasDigit;
  }

  private onPwInput(): void {
    this.pw = this.pwField?.value ?? '';
    this.error = '';
  }
  private onPw2Input(): void {
    this.pw2 = this.pw2Field?.value ?? '';
    this.error = '';
  }

  private async onSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!this.meetsPolicy) {
      this.error = msg('Please choose a password that meets the requirements.');
      return;
    }
    if (this.pw !== this.pw2) {
      this.error = msg('The passwords do not match.');
      return;
    }
    this.busy = true;
    this.error = '';
    try {
      if (this.mode === 'activate') {
        // Activation reuses the token field as the activation key.
        await endpoints.activateAccount(this.token, this.pw);
      } else {
        await endpoints.resetPassword(this.token, this.pw);
      }
      this.done = true;
    } catch (err) {
      if (err instanceof ApiError && err.code?.startsWith('password_')) {
        // Server-side policy rejection — the single-use token was NOT consumed; let them retry.
        this.error = msg('Please choose a password that meets the requirements.');
      } else if (err instanceof ApiError && (err.status === 400 || err.status === 410)) {
        this.tokenValid = false; // token expired/used while on the page
      } else {
        this.error = msg('Could not save your password. Please try again.');
      }
    } finally {
      this.busy = false;
      this.pw = '';
      this.pw2 = '';
    }
  }

  private meterColor(): string {
    return ['var(--sso-color-danger)', 'var(--sso-color-danger)', 'var(--sso-color-warning)', 'var(--sso-color-success)', 'var(--sso-color-success)'][
      this.strength
    ];
  }
  private meterLabel(): string {
    return [msg('Very weak'), msg('Weak'), msg('Fair'), msg('Good'), msg('Strong')][this.strength];
  }

  private renderInvalid() {
    return html`
      <sso-alert kind="error">
        ${msg('This link is invalid or has expired.')}
      </sso-alert>
      <p><a href="/forgot-password">${msg('Request a new link')}</a></p>
    `;
  }

  private renderDone() {
    return html`
      <sso-alert kind="success">
        ${this.mode === 'activate'
          ? msg('Your account is ready. You can now sign in.')
          : msg('Your password has been updated.')}
      </sso-alert>
      <sso-button
        variant="primary"
        @click=${() => navigate('/login')}>
        ${msg('Go to sign in')}
      </sso-button>
    `;
  }

  private renderForm() {
    const rule = (ok: boolean, text: string) => html`
      <li class=${ok ? 'ok' : ''}>
        <span aria-hidden="true">${ok ? '✓' : '•'}</span>${text}
      </li>
    `;
    return html`
      ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
      <form @submit=${this.onSubmit} novalidate>
        <sso-text-field
          name="pw"
          type="password"
          label=${msg('New password')}
          autocomplete="new-password"
          required
          ?disabled=${this.busy}
          @input-change=${this.onPwInput}></sso-text-field>
        <div>
          <div class="meter" aria-hidden="true">
            <span style=${`width:${(this.strength / 4) * 100}%;background:${this.meterColor()}`}></span>
          </div>
          <p class="meter-label">${msg('Strength:')} ${this.meterLabel()}</p>
        </div>
        <sso-text-field
          name="pw2"
          type="password"
          label=${msg('Confirm password')}
          autocomplete="new-password"
          required
          ?disabled=${this.busy}
          @input-change=${this.onPw2Input}></sso-text-field>
        <ul class="rules">
          ${rule(this.hasLength, msg(str`At least ${this.policy.minLength} characters`))}
          ${rule(this.hasMixedCase, msg('Upper and lower case letters'))}
          ${rule(this.hasDigit, msg('At least one digit'))}
          ${this.withinMax ? nothing : rule(false, msg(str`At most ${this.policy.maxLength} characters`))}
        </ul>
        <sso-button type="submit" variant="primary" ?busy=${this.busy}>
          ${msg('Save password')}
        </sso-button>
      </form>
    `;
  }

  override render() {
    const title = this.mode === 'activate' ? msg('Set your password') : msg('Set a new password');
    return html`
      <sso-card>
        <h1>${title}</h1>
        ${this.validating
          ? html`<sso-spinner aria-label=${msg('Loading')}></sso-spinner>`
          : this.done
            ? this.renderDone()
            : this.tokenValid
              ? this.renderForm()
              : this.renderInvalid()}
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'reset-password-page': ResetPasswordPage;
  }
}
